import { jsxs, jsx } from 'react/jsx-runtime';
import { twMerge } from 'tailwind-merge';
import 'react';
import SystemIcon from '../../DataDisplays/SystemIcon/SystemIcon.js';
import Typography from '../../Base/Typography/Typography.js';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';

/**
 * 사용자에게 상태 메시지를 표시하는 스낵바 컴포넌트
 *
 * @component
 * @param {Object} props
 * @param {string} props.message - 표시할 메시지 내용
 * @param {MessageType} props.type - 메시지의 유형 ('information' | 'success' | 'progress' | 'waiting' | 'caution' | 'error')
 * @param {Function} [props.onClose] - 닫기 버튼 클릭 시 실행될 콜백 함수
 *
 * @example
 * // 기본 사용법
 * <SnackBar
 *   message="저장이 완료되었습니다."
 *   type="success"
 * />
 *
 * // 닫기 버튼이 있는 경우
 * <SnackBar
 *   message="주의: 저장되지 않은 변경사항이 있습니다."
 *   type="caution"
 *   onClose={() => console.log('스낵바 닫힘')}
 * />
 */ const SnackBarIcon = ({ type })=>{
    switch(type){
        case 'information':
            return /*#__PURE__*/ jsx(SystemIcon, {
                name: "medium_information",
                className: "text-success",
                testId: "design-system-message-bar--information-icon"
            });
        case 'success':
            return /*#__PURE__*/ jsx(SystemIcon, {
                name: "medium_success",
                className: "text-success",
                testId: "design-system-message-bar--success-icon"
            });
        case 'progress':
            return /*#__PURE__*/ jsx(SystemIcon, {
                name: "medium_success",
                className: "text-progress",
                testId: "design-system-message-bar--progress-icon"
            });
        case 'waiting':
            return /*#__PURE__*/ jsx(SystemIcon, {
                name: "medium_wait",
                className: "text-waiting",
                testId: "design-system-message-bar--waiting-icon"
            });
        case 'caution':
            return /*#__PURE__*/ jsx(SystemIcon, {
                name: "medium_caution",
                className: "text-caution",
                testId: "design-system-message-bar--caution-icon"
            });
        case 'error':
            return /*#__PURE__*/ jsx(SystemIcon, {
                name: "medium_error",
                className: "text-error",
                testId: "design-system-message-bar--error-icon"
            });
        default:
            return null;
    }
};
const SnackBar = ({ message, type, onClose })=>{
    const hasCloseButton = !!onClose;
    const generateBarStyle = (type)=>{
        switch(type){
            case 'information':
                return 'bg-success-10 border-success-100';
            case 'success':
                return 'bg-success-10 border-success-100';
            case 'progress':
                return 'bg-progress-10 border-progress-100';
            case 'waiting':
                return 'bg-waiting-10 border-waiting-100';
            case 'caution':
                return 'bg-caution-10 border-caution-100';
            case 'error':
                return 'bg-error-10 border-error-100';
            default:
                return '';
        }
    };
    return /*#__PURE__*/ jsxs(Box, {
        "data-testid": "design-system-message-bar",
        className: twMerge(generateBarStyle(type), 'box-border flex w-full items-start justify-between gap-x-4 rounded-xl border px-6 py-4'),
        children: [
            /*#__PURE__*/ jsx(Box, {
                "data-testid": "design-system-message-bar--icon",
                className: "pt-[5px]",
                children: /*#__PURE__*/ jsx(SnackBarIcon, {
                    type: type
                })
            }),
            /*#__PURE__*/ jsx(Box, {
                className: "w-full",
                children: /*#__PURE__*/ jsx(Typography, {
                    variant: "body1",
                    className: "text-w-gray-600",
                    "data-testid": "design-system-message-bar--text",
                    children: message
                })
            }),
            hasCloseButton && /*#__PURE__*/ jsx(Box, {
                onClick: onClose,
                "data-testid": "design-system-message-bar--close-button",
                className: "pt-[5px]",
                children: /*#__PURE__*/ jsx(SystemIcon, {
                    name: "medium_delete",
                    className: "text-w-gray-400"
                })
            })
        ]
    });
};

export { SnackBar as default };
